import React from 'react';

type AutoResizeTextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  maxHeight?: number;
};

export const AutoResizeTextarea = React.forwardRef<HTMLTextAreaElement, AutoResizeTextareaProps>(
  ({ maxHeight = 240, style, onChange, ...props }, forwardedRef) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef]
    );

    const resize = React.useCallback(() => {
      const node = innerRef.current;
      if (!node) return;
      node.style.height = 'auto';
      const nextHeight = Math.min(node.scrollHeight, maxHeight);
      node.style.height = `${nextHeight}px`;
      node.style.overflowY = node.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, [maxHeight]);

    React.useLayoutEffect(() => {
      resize();
    }, [resize, props.value]);

    return (
      <textarea
        {...props}
        ref={setRefs}
        onChange={(e) => {
          onChange?.(e);
          resize();
        }}
        style={style}
      />
    );
  }
);

AutoResizeTextarea.displayName = 'AutoResizeTextarea';
